import React, { useEffect, useMemo, useState } from "react";
import { archiveText } from "../lib/courseHistory.js";

// Browsing is read-only and independent of the live transport and course gate.
export default function ConsultationHistory({ api, courseId, revision, MemoryView }) {
  const [courses, setCourses] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(courseId || "");
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [archive, setArchive] = useState(null);
  const [view, setView] = useState("transcript");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let alive = true;
    setCourses(null); setError("");
    api.list().then(data => {
      if (!alive) return;
      const rows = data.courses || [];
      setCourses(rows);
      setSelectedCourse(previous => rows.some(c => c.course_id === previous) ? previous :
        rows.find(c => c.course_id === courseId)?.course_id || rows[0]?.course_id || "");
    }).catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [api, courseId, revision, refresh]);
  useEffect(() => {
    let alive = true;
    setSessions(null); setSelected(null); setArchive(null);
    if (!selectedCourse || !courses) return;
    api.get(selectedCourse).then(data => {
      if (alive) setSessions([...(data.sessions || data.course?.sessions || [])].sort((a, b) => b.session_index - a.session_index));
    }).catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [api, selectedCourse, courses]);
  useEffect(() => {
    let alive = true;
    setArchive(null);
    if (selected == null) return;
    setError("");
    api.session(selectedCourse, selected).then(data => { if (alive) setArchive(data); })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [api, selectedCourse, selected, refresh]);
  const filtered = useMemo(() => (sessions || []).filter(s =>
    `${s.session_index} ${s.stage || ""} ${s.summary_abstract || ""}`.toLowerCase().includes(query.trim().toLowerCase())), [sessions, query]);
  return <section className="ap-history" aria-label="历史咨询">
    <div className="ap-history-toolbar">
      <span>历史咨询</span>
      <button className="ap-btn small" onClick={() => { setError(""); setRefresh(n => n + 1); }}>刷新</button>
    </div>
    <p className="ap-hint">查看已归档咨询的文字稿和最终记忆。浏览历史不会中断当前对话。</p>
    {error && <div className="ap-error" role="alert">{error}<br />请点击刷新重试。</div>}
    {!courses && !error && <p role="status">正在加载历史咨询…</p>}
    {courses?.length === 0 && <p className="ap-empty">暂无已归档咨询。结束咨询并归档后，可在这里查看。</p>}
    {courses?.length > 1 && <label className="ap-history-field">咨询记录组
      <select value={selectedCourse} onChange={e => { setError(""); setQuery(""); setSelectedCourse(e.target.value); }}>
        {courses.map((c, i) => <option key={c.course_id} value={c.course_id}>
          {c.course_id === courseId ? "当前记录" : `历史记录 ${i + 1}`} · {c.created_at ? new Date(c.created_at).toLocaleDateString() : c.course_id.slice(0, 8)}
        </option>)}
      </select>
    </label>}
    {selected == null ? <>
      {selectedCourse && !sessions && !error && <p role="status">正在加载会话列表…</p>}
      {!!sessions?.length && <input className="ap-history-search" aria-label="搜索历史咨询" placeholder="搜索会话序号、阶段或摘要" value={query} onChange={e => setQuery(e.target.value)} />}
      {sessions?.length === 0 && <p className="ap-empty">此记录暂无已归档咨询。</p>}
      {!!sessions?.length && !filtered.length && <p className="ap-empty">没有匹配的咨询记录。</p>}
      {filtered.map(s => <button key={s.session_index} className="ap-history-card" onClick={() => { setSelected(s.session_index); setView("transcript"); }}>
        <strong>第 {s.session_index} 次咨询 · {s.stage || "已归档"}</strong>
        <span>{s.turns || 0} 条对话</span>
        <span>{s.summary_abstract || "查看文字稿与归档记忆"}</span>
      </button>)}
    </> : <>
      <button className="ap-btn" onClick={() => { setSelected(null); setError(""); }}>← 返回历史列表</button>
      <strong>第 {selected} 次咨询</strong>
      <div className="ap-history-tabs" role="group" aria-label="归档内容">
        <button className={"ap-btn" + (view === "transcript" ? " primary" : "")} aria-pressed={view === "transcript"} onClick={() => setView("transcript")}>文字稿</button>
        <button className={"ap-btn" + (view === "memory" ? " primary" : "")} aria-pressed={view === "memory"} onClick={() => setView("memory")}>归档记忆</button>
      </div>
      {!archive && !error && <p role="status">正在读取归档…</p>}
      {archive && view === "transcript" && <div className="ap-history-transcript">
        {!archive.transcript?.length && <p className="ap-empty">此咨询未保存文字稿。</p>}
        {(archive.transcript || []).map((turn, i) => <div className="ap-history-turn" key={i}>
          <strong>{turn.speaker === "counselor" ? "咨询师" : "来访者"}</strong>
          <div>{archiveText(turn.text) || "（无文本）"}</div>
        </div>)}
      </div>}
      {archive && view === "memory" && <>
        <p className="ap-hint">以下为该次咨询归档完成时保存的记忆。</p>
        <details className="ap-section" open><summary>咨询总结与下次计划</summary><div className="ap-section-body"><MemoryView obj={archive.summary} /></div></details>
        <details className="ap-section" open><summary>归档后的来访者档案</summary><div className="ap-section-body"><MemoryView obj={archive.profile} /></div></details>
      </>}
    </>}
  </section>;
}
